import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faFileLines,
  faFolderOpen,
  faArrowUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';

const DOCUMENT_GENERATION_API_BASE = (
  process.env.REACT_APP_DOCUMENT_GENERATION_API_BASE
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/documentGenerationApi'
);

const DOCUMENT_GENERATION_SHARED_SECRET = process.env.REACT_APP_DOCUMENT_GENERATION_SHARED_SECRET || '';

const DOCUMENT_TYPE_LABELS = {
  recommendation: 'Recommendation Letter',
  confirmation: 'Student Confirmation Letter',
  attachment: 'Attachment / Internship Letter',
  boardResolution: 'Board Resolution Letter',
  minutes: 'Minutes Letter',
  custom: 'Custom Business Letter'
};

const normalizeText = (value) => String(value || '').trim();

const formatDocumentDate = (value, fallback = 'Not dated') => {
  if (!value) return fallback;

  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString('en-KE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  }

  if (value?.toDate) {
    return value.toDate().toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  const timestampSeconds = Number(value?.seconds ?? value?._seconds);
  const timestampMilliseconds = Number(value?.milliseconds ?? value?._milliseconds);

  if (Number.isFinite(timestampSeconds)) {
    return new Date(timestampSeconds * 1000).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  if (Number.isFinite(timestampMilliseconds)) {
    return new Date(timestampMilliseconds).toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  return fallback;
};

function GeneratedDocuments() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [documentType, setDocumentType] = useState('all');
  const [lastLoadedAt, setLastLoadedAt] = useState('');

  const loadDocuments = async ({ isRefresh = false } = {}) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      const params = new URLSearchParams({ limit: '50' });
      const query = normalizeText(searchTerm);
      if (query) {
        params.set('q', query);
      }
      if (documentType !== 'all') {
        params.set('documentType', documentType);
      }

      const response = await fetch(
        `${DOCUMENT_GENERATION_API_BASE}/documents?${params.toString()}`,
        {
          headers: {
            ...(DOCUMENT_GENERATION_SHARED_SECRET
              ? { 'x-document-generation-secret': DOCUMENT_GENERATION_SHARED_SECRET }
              : {})
          }
        }
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Unable to load generated documents');
      }

      setDocuments(Array.isArray(data.documents) ? data.documents : []);
      setLastLoadedAt(new Date().toLocaleString('en-KE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }));
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        setError(requestError?.message || 'Failed to load generated documents');
        setDocuments([]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadDocuments();
    }, 200);

    return () => window.clearTimeout(timer);
  }, [searchTerm, documentType]);

  const refreshDocuments = async () => {
    await loadDocuments({ isRefresh: true });
  };

  const filteredSummary = useMemo(() => {
    const studentTypes = new Set(['recommendation', 'confirmation', 'attachment']);
    return {
      total: documents.length,
      student: documents.filter((item) => studentTypes.has(item.documentType)).length,
      business: documents.filter((item) => !studentTypes.has(item.documentType)).length,
      withReference: documents.filter((item) => normalizeText(item.referenceNumber)).length
    };
  }, [documents]);

  const openDocument = (document) => {
    if (!document?.id) {
      return;
    }

    navigate(`/generated-documents/${document.id}`);
  };

  const documentTypeOptions = useMemo(() => ([
    { value: 'all', label: 'All documents' },
    ...Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({ value, label }))
  ]), []);

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="clientsComp applicationsPage businessInfoPage">
          <div className="appsHeader">
            <div>
              <p className="appsKicker">Business Info</p>
              <h4>Generated Documents</h4>
              <p className="appsSubtext">
                Browse documents saved from the generator, review the latest versions, and reopen any file in its rendered format.
              </p>
            </div>
            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Total</span>
                <strong>{filteredSummary.total}</strong>
              </div>
              <div className="appsStatChip">
                <span>Student</span>
                <strong>{filteredSummary.student}</strong>
              </div>
              <div className="appsStatChip">
                <span>Business</span>
                <strong>{filteredSummary.business}</strong>
              </div>
              <div className="appsStatChip">
                <span>Reference</span>
                <strong>{filteredSummary.withReference}</strong>
              </div>
            </div>
          </div>

          <div className="appsInsightsCard">
            <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
              <div>
                <p className="appsKicker">Saved output</p>
                <h6 className="mb-1">
                  <FontAwesomeIcon icon={faFileLines} className="me-2" />
                  Document archive
                </h6>
                <p className="appsSubtext mb-0">
                  Open the exact HTML version that was saved, then continue refining the source in the generator if needed.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <span className="cohortStatusBadge cohortStatusBadge--joined">
                  <FontAwesomeIcon icon={faFolderOpen} className="me-1" />
                  {lastLoadedAt ? `Loaded ${lastLoadedAt}` : 'Not loaded yet'}
                </span>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard">
            <Button variant="outline-dark" onClick={refreshDocuments} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button variant="outline-dark" onClick={() => navigate('/document-generator')}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="me-2" />
              Open Generator
            </Button>
          </div>

          <div className="appsFiltersCard appsFiltersCard--stacked">
            <Form.Group className="flex-grow-1">
              <Form.Label>Search documents</Form.Label>
              <Form.Control
                type="search"
                placeholder="Search by student, reference, prompt, or subject"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="ms-0 ms-md-3" style={{ minWidth: '240px' }}>
              <Form.Label>Document type</Form.Label>
              <Form.Select value={documentType} onChange={(event) => setDocumentType(event.target.value)}>
                {documentTypeOptions.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <div className="appsTableCard">
            <div className="appsTableHeader">
              <h6>Saved documents</h6>
              <span>{documents.length} item{documents.length === 1 ? '' : 's'}</span>
            </div>
            <div className="table-responsive">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status" />
                  <p>Loading generated documents...</p>
                </div>
              ) : error ? (
                <div className="incomeStateCard incomeEmptyState">
                  <h6>Could not load documents</h6>
                  <p>{error}</p>
                </div>
              ) : documents.length === 0 ? (
                <div className="incomeStateCard incomeEmptyState">
                  <h6>No documents found</h6>
                  <p>Generate and save a document first, then it will appear here.</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Type</th>
                      <th>Student / Subject</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((item) => {
                      const documentLabel = DOCUMENT_TYPE_LABELS[item.documentType] || item.documentLabel || item.documentType || 'Document';
                      const subject = item.subject || item.document?.subject || 'Untitled';
                      const studentLabel = item.studentName || item.document?.studentName || '—';
                      const secondaryLabel = item.course || item.businessSubject || item.meetingTitle || item.admissionNumber || '—';

                      return (
                        <tr key={item.id}>
                          <td>{item.referenceNumber || '—'}</td>
                          <td>{documentLabel}</td>
                          <td>
                            <div className="d-flex flex-column">
                              <strong>{studentLabel}</strong>
                              <span className="text-muted">{secondaryLabel}</span>
                              <small className="text-muted">{subject}</small>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              <strong>{formatDocumentDate(item.createdAt || item.updatedAt)}</strong>
                              <small className="text-muted">Saved on</small>
                            </div>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-dark"
                              onClick={() => openDocument(item)}
                              disabled={!item.id}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneratedDocuments;
